<template>
    <div>
        <v-card-text>
            <v-row dense align="center">
                <v-col cols="12" md="8">
                    <div class="text-caption grey--text text--darken-1">
                        Imágenes que aparecen en una ventana emergente al ingresar (login) o en el inicio del participante. Se muestran en el orden
                        indicado; la frecuencia controla cuántas veces las ve cada persona en su navegador.
                    </div>
                </v-col>
                <v-col cols="12" md="4" class="text-md-right">
                    <v-btn color="info" small outlined class="mr-2" :loading="cargando" @click="cargar"><v-icon small>refresh</v-icon></v-btn>
                    <v-btn color="success" small outlined v-if="permisos.crear" @click="nuevo"><v-icon small left>add</v-icon> Nueva imagen</v-btn>
                </v-col>
            </v-row>

            <v-data-table
                dense
                class="mt-3"
                :headers="encabezados"
                :items="filas"
                :loading="cargando"
                item-key="slide_id"
                :items-per-page="-1"
                hide-default-footer
                disable-sort
            >
                <template v-slot:[`item.orden`]="{ index }">
                    <div class="d-flex align-center">
                        <span class="mr-1 text-caption grey--text">{{ index + 1 }}</span>
                        <v-btn icon x-small :disabled="index === 0 || ordenando || !permisos.editar" title="Subir" @click="mover(index, -1)"><v-icon small>arrow_upward</v-icon></v-btn>
                        <v-btn icon x-small :disabled="index === filas.length - 1 || ordenando || !permisos.editar" title="Bajar" @click="mover(index, 1)"><v-icon small>arrow_downward</v-icon></v-btn>
                    </div>
                </template>
                <template v-slot:[`item.imagen_url`]="{ item }">
                    <a v-if="item.imagen_url" :href="item.imagen_url" target="_blank" rel="noopener" title="Abrir imagen">
                        <img :src="item.imagen_url" :alt="item.titulo" class="miniatura">
                    </a>
                    <span v-else class="error--text text-caption">Imagen no encontrada</span>
                </template>
                <template v-slot:[`item.titulo`]="{ item }">
                    <div class="font-weight-medium">{{ item.titulo }}</div>
                    <a v-if="item.enlace" :href="item.enlace" target="_blank" rel="noopener" class="text-caption">{{ item.enlace }}</a>
                </template>
                <template v-slot:[`item.vigencia`]="{ item }">
                    <template v-if="item.fecha_desde || item.fecha_hasta">
                        {{ fechaCorta(item.fecha_desde, 'Sin inicio') }} – {{ fechaCorta(item.fecha_hasta, 'Sin fin') }}
                    </template>
                    <span v-else class="grey--text">Sin límite</span>
                </template>
                <template v-slot:[`item.activo`]="{ item }">
                    <v-chip x-small label :color="item.activo ? (item.vigente ? 'success' : 'warning') : 'grey'" text-color="white">
                        {{ item.activo ? (item.vigente ? 'Vigente' : 'Activa, no vigente') : 'Inactiva' }}
                    </v-chip>
                </template>
                <template v-slot:[`item.acciones`]="{ item }">
                    <v-btn icon small color="info" v-if="permisos.editar" title="Editar" @click="editar(item)"><v-icon small>edit</v-icon></v-btn>
                    <v-btn icon small color="error" v-if="permisos.eliminar" title="Eliminar" @click="confirmarEliminar(item)"><v-icon small>delete</v-icon></v-btn>
                </template>
                <template v-slot:no-data>
                    <v-alert dense type="info" outlined class="text-center mt-3">
                        No hay imágenes en el slider. {{ permisos.crear ? 'Use el botón Nueva imagen para agregar la primera.' : '' }}
                    </v-alert>
                </template>
            </v-data-table>
        </v-card-text>

        <!-- Formulario -->
        <v-dialog v-model="dialogo" max-width="720" persistent scrollable>
            <v-card>
                <v-card-title class="text-subtitle-1 font-weight-bold">
                    {{ modelo.slide_id ? 'Editar' : 'Nueva' }} imagen
                    <v-spacer></v-spacer>
                    <v-btn icon small @click="dialogo = false"><v-icon>close</v-icon></v-btn>
                </v-card-title>
                <v-card-text>
                    <v-form ref="form" v-model="valido" @submit.prevent="guardar">
                        <v-row dense>
                            <v-col cols="12" md="8">
                                <v-text-field v-model="modelo.titulo" label="Título" outlined dense maxlength="150" :rules="[reglas.requerido]" placeholder="Ej. Inscripciones abiertas"/>
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-text-field v-model="modelo.orden" label="Orden" type="number" min="0" outlined dense :rules="[reglas.entero]" hint="Menor número, primero." persistent-hint/>
                            </v-col>
                            <v-col cols="12">
                                <v-file-input v-model="imagen" outlined dense accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp" show-size truncate-length="48" prepend-icon="image"
                                              :label="modelo.slide_id ? 'Reemplazar imagen (JPG, PNG o WEBP)' : 'Imagen (JPG, PNG o WEBP)'"
                                              :rules="[reglas.imagen]" hint="Máximo 3 MB. Se recomienda formato horizontal, por ejemplo 1200 × 500 px." persistent-hint
                                              @change="previsualizar"/>
                            </v-col>
                            <v-col cols="12" v-if="vistaPrevia">
                                <img :src="vistaPrevia" alt="Vista previa" class="vista-previa">
                            </v-col>
                            <v-col cols="12">
                                <v-text-field v-model="modelo.enlace" label="Enlace al pulsar la imagen (opcional)" outlined dense prepend-icon="link"
                                              placeholder="https://..." :rules="[reglas.url]"/>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-select v-model="modelo.contexto" :items="contextos" item-text="texto" item-value="valor" label="Dónde se muestra" outlined dense :rules="[reglas.requerido]"/>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-select v-model="modelo.frecuencia" :items="frecuencias" item-text="texto" item-value="valor" label="Frecuencia por persona" outlined dense :rules="[reglas.requerido]"/>
                            </v-col>
                            <v-col cols="6">
                                <v-text-field v-model="modelo.fecha_desde" label="Desde" type="date" outlined dense clearable/>
                            </v-col>
                            <v-col cols="6">
                                <v-text-field v-model="modelo.fecha_hasta" label="Hasta" type="date" outlined dense clearable :rules="[reglas.fechas]"/>
                            </v-col>
                            <v-col cols="12">
                                <v-switch v-model="modelo.activo" inset hide-details color="success" class="mt-0"
                                          :label="modelo.activo ? 'Activa: se muestra en el slider' : 'Inactiva: se conserva pero no se muestra'"/>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>
                <v-card-actions class="px-6 pb-4">
                    <v-spacer></v-spacer>
                    <v-btn text @click="dialogo = false">Cancelar</v-btn>
                    <v-btn color="primary" depressed :loading="guardando" @click="guardar"><v-icon small left>save</v-icon> Guardar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="dialogoEliminar" max-width="420">
            <v-card>
                <v-card-title class="text-subtitle-1 font-weight-bold">Eliminar imagen</v-card-title>
                <v-card-text>¿Eliminar <b>{{ aEliminar ? aEliminar.titulo : '' }}</b> del slider? Se borrará también el archivo de la imagen.</v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="dialogoEliminar = false">Cancelar</v-btn>
                    <v-btn color="error" depressed :loading="guardando" @click="eliminar">Eliminar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import filtros from "@/filters";

const MAXIMO_MB = 3;

/** Pestaña Slider: CRUD de las imágenes del slider/popup del ingreso y el inicio. */
export default {
    name: "VideosSlider",
    data() {
        return {
            cargando: false,
            guardando: false,
            ordenando: false,
            filas: [],
            contextos: [],
            frecuencias: [],
            permisos: {crear: false, editar: false, eliminar: false},
            dialogo: false,
            dialogoEliminar: false,
            valido: true,
            modelo: this.vacio(),
            imagen: null,
            vistaPrevia: null,
            aEliminar: null,
            encabezados: [
                {text: "Orden", value: "orden", width: 110},
                {text: "Imagen", value: "imagen_url", width: 130},
                {text: "Título", value: "titulo"},
                {text: "Dónde", value: "contexto_texto"},
                {text: "Frecuencia", value: "frecuencia_texto"},
                {text: "Vigencia", value: "vigencia"},
                {text: "Estado", value: "activo"},
                {text: "", value: "acciones", align: "end"},
            ],
            reglas: {
                requerido: v => (v !== null && v !== undefined && String(v).trim() !== "") || "Campo requerido",
                entero: v => v === null || v === "" || v === undefined || (Number.isInteger(Number(v)) && Number(v) >= 0) || "Número entero",
                url: v => !v || /^https?:\/\/\S+$/i.test(String(v).trim()) || "Debe empezar con http:// o https://",
                fechas: v => !v || !this.modelo.fecha_desde || v >= this.modelo.fecha_desde || "Debe ser igual o posterior a la fecha desde",
                imagen: v => {
                    if (this.modelo.slide_id && !v) return true;
                    if (!v) return "Seleccione la imagen";
                    if (v.size > MAXIMO_MB * 1024 * 1024) return `La imagen no puede superar ${MAXIMO_MB} MB`;
                    return true;
                },
            },
        }
    },
    methods: {
        fechaCorta(fecha, vacio) {
            return fecha ? filtros.fechaESP(fecha) : vacio;
        },
        vacio() {
            return {slide_id: null, titulo: "", enlace: "", orden: null, activo: true, fecha_desde: null, fecha_hasta: null, contexto: "ambos", frecuencia: "siempre"};
        },
        cargar() {
            this.cargando = true;
            window.axios.get("/slides/admin").then(response => {
                if (response.data.success) {
                    const d = response.data.data;
                    this.filas = d.filas;
                    this.contextos = d.contextos;
                    this.frecuencias = d.frecuencias;
                    this.permisos = d.permisos;
                } else {
                    this.$toast.error(response.data.message);
                }
            })
                .catch(() => this.$toast.error("No se pudieron listar las imágenes."))
                .finally(() => {
                    this.cargando = false;
                });
        },
        nuevo() {
            this.modelo = this.vacio();
            this.imagen = null;
            this.vistaPrevia = null;
            this.dialogo = true;
            this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
        },
        editar(fila) {
            this.modelo = {
                ...this.vacio(), ...fila,
                activo: !!fila.activo,
                enlace: fila.enlace || "",
                fecha_desde: fila.fecha_desde ? String(fila.fecha_desde).slice(0, 10) : null,
                fecha_hasta: fila.fecha_hasta ? String(fila.fecha_hasta).slice(0, 10) : null,
            };
            this.imagen = null;
            this.vistaPrevia = fila.imagen_url;
            this.dialogo = true;
            this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
        },
        previsualizar(archivo) {
            if (!archivo) {
                this.vistaPrevia = this.modelo.imagen_url || null;
                return;
            }
            const lector = new FileReader();
            lector.onload = () => {
                this.vistaPrevia = lector.result;
            };
            lector.readAsDataURL(archivo);
        },
        guardar() {
            if (!this.$refs.form.validate()) return;

            this.guardando = true;
            const m = this.modelo;
            const formData = new FormData();
            formData.append("slide_id", m.slide_id || "");
            formData.append("titulo", m.titulo.trim());
            formData.append("contexto", m.contexto);
            formData.append("frecuencia", m.frecuencia);
            formData.append("activo", m.activo ? 1 : 0);
            if (m.enlace && m.enlace.trim()) formData.append("enlace", m.enlace.trim());
            if (m.orden !== null && m.orden !== "" && m.orden !== undefined) formData.append("orden", m.orden);
            if (m.fecha_desde) formData.append("fecha_desde", m.fecha_desde);
            if (m.fecha_hasta) formData.append("fecha_hasta", m.fecha_hasta);
            if (this.imagen) formData.append("imagen", this.imagen);

            window.axios.post("/slides/guardar", formData).then(response => {
                if (response.data.success) {
                    this.$toast.success(response.data.message);
                    this.dialogo = false;
                    this.cargar();
                } else {
                    this.$toast.error(response.data.message, {timeout: 10000});
                }
            })
                .catch(error => this.$toast.error(this.mensajeDe(error, "No se pudo guardar la imagen."), {timeout: 10000}))
                .finally(() => {
                    this.guardando = false;
                });
        },
        /** Intercambia la fila con la vecina y envía el orden completo. */
        mover(indice, paso) {
            const destino = indice + paso;
            if (destino < 0 || destino >= this.filas.length) return;

            const filas = [...this.filas];
            [filas[indice], filas[destino]] = [filas[destino], filas[indice]];
            this.filas = filas;

            this.ordenando = true;
            window.axios.post("/slides/ordenar", {orden: filas.map(f => f.slide_id)}).then(response => {
                if (!response.data.success) this.$toast.error(response.data.message);
                this.cargar();
            })
                .catch(error => {
                    this.$toast.error(this.mensajeDe(error, "No se pudo guardar el orden."));
                    this.cargar();
                })
                .finally(() => {
                    this.ordenando = false;
                });
        },
        confirmarEliminar(fila) {
            this.aEliminar = fila;
            this.dialogoEliminar = true;
        },
        eliminar() {
            this.guardando = true;
            window.axios.post("/slides/eliminar", {slide_id: this.aEliminar.slide_id}).then(response => {
                if (response.data.success) {
                    this.$toast.success(response.data.message);
                    this.dialogoEliminar = false;
                    this.cargar();
                } else {
                    this.$toast.error(response.data.message, {timeout: 8000});
                }
            })
                .catch(error => this.$toast.error(this.mensajeDe(error, "No se pudo eliminar la imagen."), {timeout: 8000}))
                .finally(() => {
                    this.guardando = false;
                });
        },
        mensajeDe(error, porDefecto) {
            const cuerpo = error.response && error.response.data;
            if (!cuerpo) return porDefecto;
            if (cuerpo.data && typeof cuerpo.data === "object") {
                const primero = Object.values(cuerpo.data).flat()[0];
                if (primero) return primero;
            }
            return cuerpo.message || porDefecto;
        },
    },
    created() {
        this.cargar();
    },
}
</script>

<style lang="scss" scoped>
.miniatura {
    display: block;
    width: 110px;
    height: 46px;
    object-fit: cover;
    border-radius: 6px;
    border: 1px solid #e3e7f2;
    margin: 4px 0;
}

.vista-previa {
    display: block;
    max-width: 100%;
    max-height: 220px;
    border-radius: 8px;
    border: 1px solid #e3e7f2;
}
</style>
